/**
 * Per-org splash hint — a tiny localStorage record so the parse-time loading
 * splash (inline #splash in index.html + React LoadingSplash) can paint the
 * org's background color BEFORE the org config loads, killing the white flash
 * on dark orgs.
 *
 * The splash shows before namespace resolution, so the key is derived from
 * `location.pathname` alone (first two path segments). Stale-theme cost: one
 * wrong-color splash after an org changes its theme, then it self-heals on the
 * next load (the writer re-stamps the current theme).
 *
 * Pure + jest-safe (no firebase / React / import.meta). The inline head script
 * in index.html hand-duplicates the key prefix + exclusion regex below — the
 * `splashHint.test.ts` sync guard reads index.html and asserts they match, so
 * if you change either constant, update index.html in the same commit.
 */

export type SplashMode = "light" | "dark";
export type SplashHint = { bg: string; mode: SplashMode };

/** localStorage key prefix. KEEP IN SYNC with index.html's inline script. */
export const SPLASH_HINT_KEY_PREFIX = "orgiggly:splash:";

/**
 * Non-org pathname exclusions (root is handled separately). Same set as the
 * favicon / manifest inline scripts in index.html. KEEP `.source` IN SYNC with
 * index.html's inline script.
 */
export const SPLASH_EXCLUDE_RE = /^\/(admin|embed|demo|terms|privacy)(\/|$)/;

/**
 * Allowed `bg` shapes — hex (#rgb/#rgba/#rrggbb/#rrggbbaa) or rgb()/rgba().
 * Validated on READ so a corrupt or hostile localStorage entry can't inject CSS
 * via the `--splash-bg` custom property or the LoadingSplash `sx`.
 */
const SAFE_COLOR_RE =
  /^#(?:[0-9a-fA-F]{3,4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$|^rgba?\(\s*[\d.,\s%]+\)$/;

/**
 * The storage key for a pathname, or `undefined` for non-org pages (root,
 * /admin, /embed, /demo, /terms, /privacy). Keys to the first two path
 * segments: `/dave/worldcup2026` → `…:/dave/worldcup2026`, `/worldcup` →
 * `…:/worldcup`, `/mock/wang/1989` → `…:/mock/wang`.
 */
export const splashHintKey = (pathname: string): string | undefined => {
  if (pathname === "/" || SPLASH_EXCLUDE_RE.test(pathname)) return undefined;
  const segments = pathname.split("/").filter(Boolean);
  if (segments.length === 0) return undefined;
  return SPLASH_HINT_KEY_PREFIX + "/" + segments.slice(0, 2).join("/");
};

const isSafeColor = (v: unknown): v is string =>
  typeof v === "string" && SAFE_COLOR_RE.test(v);

const isMode = (v: unknown): v is SplashMode => v === "light" || v === "dark";

/** Read the hint for a pathname. Returns undefined on miss / corrupt / unsafe. */
export const readSplashHint = (pathname: string): SplashHint | undefined => {
  const key = splashHintKey(pathname);
  if (!key) return undefined;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return undefined;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return undefined;
    const { bg, mode } = parsed as Record<string, unknown>;
    if (!isSafeColor(bg) || !isMode(mode)) return undefined;
    return { bg, mode };
  } catch {
    // Corrupt JSON, unsafe value, or storage unavailable → neutral splash.
    return undefined;
  }
};

/** Persist the hint for a pathname. No-op for non-org pages / storage errors. */
export const writeSplashHint = (pathname: string, hint: SplashHint): void => {
  const key = splashHintKey(pathname);
  if (!key) return;
  try {
    localStorage.setItem(key, JSON.stringify({ bg: hint.bg, mode: hint.mode }));
  } catch {
    // Quota / disabled storage → skip; the splash just stays neutral.
  }
};
